import type { AssetLoader } from '@/shared/types/assets'

/**
 * 资产加载器注册表。
 * 按 format 管理加载器，支持自动检测。
 */
export class LoaderRegistry<TSource, TResult> {
  private loaders = new Map<string, AssetLoader<TSource, TResult>>()

  /**
   * 注册加载器。
   *
   * @param loader - 待注册加载器，format 不得为空且唯一
   * @throws format 已注册时抛 Error
   */
  register(loader: AssetLoader<TSource, TResult>): void {
    if (!loader.format) {
      throw new Error('LoaderRegistry: format 不能为空')
    }
    if (this.loaders.has(loader.format)) {
      throw new Error(`LoaderRegistry: format "${loader.format}" 已注册`)
    }
    this.loaders.set(loader.format, loader)
  }

  /**
   * 按 format 精确查找加载器。
   *
   * @param format - 目标格式标识
   * @returns 对应加载器，未注册返回 undefined
   */
  get(format: string): AssetLoader<TSource, TResult> | undefined {
    return this.loaders.get(format)
  }

  /**
   * 自动检测：遍历所有 loader 的 canLoad()。
   *
   * 多个匹配时按 detectPriority 降序选最高；同优先级多个匹配时抛 Error。
   *
   * @param source - 待识别来源
   * @returns 唯一匹配的加载器，无匹配时返回 undefined
   */
  detect(source: TSource): AssetLoader<TSource, TResult> | undefined {
    const matches: AssetLoader<TSource, TResult>[] = []
    this.loaders.forEach((loader) => {
      if (loader.canLoad?.(source)) {
        matches.push(loader)
      }
    })
    if (matches.length === 0) return undefined
    if (matches.length === 1) return matches[0]

    matches.sort((a, b) => (b.detectPriority ?? 0) - (a.detectPriority ?? 0))
    const top = matches[0]
    const second = matches[1]
    if ((top.detectPriority ?? 0) === (second.detectPriority ?? 0)) {
      throw new Error(
        'LoaderRegistry: 多个 loader 匹配且 detectPriority 相同 (' +
          top.format +
          ', ' +
          second.format +
          ')，请指定 format'
      )
    }
    return top
  }

  /**
   * 返回所有已注册的 format 列表。
   *
   * @returns 格式字符串数组
   */
  formats(): string[] {
    return Array.from(this.loaders.keys())
  }
}
